import get from 'lodash.get';

// Ported from MyAccountWeb-FE src/app/helpers/outageHelpers.js.
// Pure helpers that normalize outage / device-status / ITG data per line-of-business.

// Maps a line-of-business to the device-status service keys it owns.
export const lobServiceMap = {
  tv: ['video'],
  video: ['video'],
  internet: ['internet'],
  voice: ['voice'],
  flex: ['internet'],
};

// Device statuses that indicate a local (device-level) problem.
export const unreachableStatusMap = {
  UNREACHABLE: true,
  ISSUE_DETECTED: true,
  PROVISIONED_UNREACHABLE: true,
  UNKNOWN: true,
  OFFLINE: true,
};

// Line-of-business → outage summary keys that count as an outage for that LOB.
const outageLobMap = {
  video: ['tvOutage', 'channelOutage'],
  home: ['homeOutage'],
  internet: ['internetOutage'],
  voice: ['voiceOutage'],
  flex: ['internetOutage'],
  instantTV: [],
  internetVoice: ['voiceOutage', 'internetOutage'],
};

// Document IDs specific to a particular line-of-business issue.
const specificIssues = {
  ITG881: 'internet',
  ITG923: 'internet',
  ITG924: 'internet',
  ITG937: 'video',
  ITG1063: 'video',
  ITG1075: 'voice',
  ITG1138: 'video',
  ITG1152: 'voice',
  ITG1153: 'home',
  ITG1194: 'video',
  ITG1219: 'home',
  ITG1220: 'home',
  ITG1342: 'internet',
  ITG1346: 'voice',
  ITG1382: 'home',
  ITG1394: 'video',
  ITG1403: 'home',
  ITG1505: 'internet',
  ITG1550: 'home',
  ITG1570: 'home',
  ITG1604: 'internet',
};

// Reduces an outage summary object into an array of the truthy outage keys.
export const reduceOutageSummary = (summary = {}) => Object.keys(summary).reduce(
  (result, key) => {
    if (summary[key]) {
      result.push(key);
    }
    return result;
  },
  [],
);

// Returns true when the reduced outage summary contains an outage for the LOB.
export const serviceHasOutage = (rawLob, summary = []) => {
  let lob = rawLob;
  if (lob === 'storeFront') {
    lob = 'TV & Streaming';
  }
  if (!outageLobMap[lob]) {
    return false;
  }
  return summary.some(outage => outageLobMap[lob].indexOf(outage) > -1);
};

// Returns true when the given device state is one of the unreachable states.
export const isUnreachableState = status => !!unreachableStatusMap[status];

// Returns true when a device for the LOB reports a local (device-level) problem.
export const serviceHasLocalProblem = (
  rawLob = null,
  deviceStatus = {},
  useSsmDeviceStatus = true,
) => {
  if (useSsmDeviceStatus) {
    const services = get(deviceStatus, 'services', {});
    const keys = rawLob
      ? [lobServiceMap[rawLob.toLowerCase()] || '']
      : Object.keys(services);
    return keys.some((service) => {
      const devices = get(services, `${service}.devices`, []);
      return devices.some(device => isUnreachableState(get(device, 'status.state')));
    });
  }
  const { hic: { status, services } = {} } = deviceStatus;
  if (!services) return false;
  if (!rawLob) {
    return isUnreachableState(status);
  }
  const lob = rawLob.toLowerCase();
  if (!lobServiceMap[lob]) {
    return false;
  }
  return lobServiceMap[lob].some(
    service => isUnreachableState((services[service] || {}).status),
  );
};

// Finds the ITG StartAction in a recommendation's action list.
const findStartAction = (actions = []) => actions.find(
  action => action.actionType === 'ITG_1.0',
);

// Returns the highest-priority recommendation (lowest numeric priority).
const highestPriority = (recommendations = []) => recommendations.reduce(
  (best, current) => {
    if (!best || (current && current.priority < best.priority)) {
      return current;
    }
    return best;
  },
  null,
);

// Ignore markers can be embedded in ITG copy; strip them out.
const ignoreCopy = value => (/\s*IGNORE\s*/.test(value) ? undefined : value);

// Parses an ITG recommendations response into a normalized problem descriptor.
export const parseItg = ({ recommendations = [], cxpSessionId } = {}, outageType) => {
  const recommendation = highestPriority(recommendations) || {};
  const {
    actions,
    shortDescription,
    longDescription,
    recommendationsName,
  } = recommendation;
  const { actionIdentifier } = (actions && findStartAction(actions)) || {};
  if (!actionIdentifier) {
    return undefined;
  }
  const docId = actionIdentifier.split(',')[0].toUpperCase();
  if (outageType && specificIssues[docId] && specificIssues[docId] !== outageType) {
    return undefined;
  }
  return {
    name: ignoreCopy(shortDescription),
    description: ignoreCopy(longDescription),
    link: `https://www.xfinity.com/support/repair/guided/${cxpSessionId}/${docId}`,
    linkText: 'Let’s Fix It',
    docId,
    recommendationsName,
    outageType: specificIssues[docId] || 'internet',
  };
};
